import { useEffect, useRef, useState, useCallback } from "react"
import { useMapsLibrary } from "@vis.gl/react-google-maps"

/**
 * A reusable hook that retrieves autocomplete suggestions from the Google Places API.
 * Uses the new Autocomplete Data API (Places API New).
 *
 * @see https://developers.google.com/maps/documentation/javascript/place-autocomplete-data
 *
 * @param {string} inputString - The input string for which to fetch autocomplete suggestions.
 * @param {Object} requestOptions - Additional options for the autocomplete request.
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 300ms).
 *
 * @returns {Object} An object containing:
 *   - suggestions: Array of autocomplete suggestions
 *   - isLoading: Boolean indicating if a request is in progress
 *   - resetSession: Function to reset the session token (call after place selection)
 *
 * @example
 * ```jsx
 * const { suggestions, isLoading, resetSession } = useAutocompleteSuggestions(input, {
 *   includedRegionCodes: ['US', 'CA']
 * });
 * ```
 */
export function useAutocompleteSuggestions(
  inputString,
  requestOptions = {},
  debounceMs = 300,
) {
  const placesLib = useMapsLibrary("places")

  // stores the current sessionToken
  const sessionTokenRef = useRef(null)

  // debounce timer reference
  const debounceTimerRef = useRef(null)

  // the suggestions based on the specified input
  const [suggestions, setSuggestions] = useState([])

  // indicates if there is currently an incomplete request to the places API
  const [isLoading, setIsLoading] = useState(false)

  // Reset session - call this after a place is selected and fetchFields is called
  const resetSession = useCallback(() => {
    sessionTokenRef.current = null
    setSuggestions([])
  }, [])

  // Once the PlacesLibrary is loaded and whenever the input changes, a query
  // is sent to the Autocomplete Data API with debouncing.
  useEffect(() => {
    if (!placesLib) return undefined

    const { AutocompleteSessionToken, AutocompleteSuggestion } = placesLib

    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // If input is empty, clear suggestions immediately
    if (inputString === "" || !inputString) {
      if (suggestions.length > 0) setSuggestions([])
      setIsLoading(false)
      return undefined
    }

    setIsLoading(true)

    // Debounce the API call
    debounceTimerRef.current = setTimeout(() => {
      // Create a new session if one doesn't already exist. This has to be reset
      // after `fetchFields` for one of the returned places is called by calling
      // the `resetSession` function returned from this hook.
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new AutocompleteSessionToken()
      }

      const request = {
        ...requestOptions,
        input: inputString,
        sessionToken: sessionTokenRef.current,
      }

      AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
        .then((res) => {
          setSuggestions(res.suggestions || [])
          setIsLoading(false)
        })
        .catch((error) => {
          console.error("Error fetching autocomplete suggestions:", error)
          setSuggestions([])
          setIsLoading(false)
        })
    }, debounceMs)

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [placesLib, inputString, debounceMs, requestOptions])

  return {
    suggestions,
    isLoading,
    resetSession,
  }
}

export default useAutocompleteSuggestions
